/**
 * Public Compliance / Trust Center page.
 *
 * Anonymous companion to `/admin/trust` — the version a prospect, security
 * reviewer or procurement officer can scroll through without an account.
 *
 * Sources: `/api/trust/public` (open, cached at the edge). Audit-pack and DPA
 * downloads remain `/api/trust/audit-pack.pdf` and `/api/trust/dpa.pdf` and
 * are gated to authenticated tenant users; we surface a friendly login CTA
 * for anonymous visitors so they understand why the click 401s.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ShieldCheck, Lock, FileText, Download, Globe, MapPin, Mail,
    Server, Loader2, AlertTriangle, CheckCircle2, ExternalLink, Sparkles,
} from "lucide-react";
import { VERSION } from "@/lib/version";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
    compliant: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Compliant" },
    controls_implemented: { cls: "bg-cyan-100 text-cyan-800 border-cyan-200", label: "Controls implemented" },
    aligned: { cls: "bg-indigo-100 text-indigo-800 border-indigo-200", label: "Aligned" },
    in_progress: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "In progress" },
};

export default function Compliance() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/trust/public`)
            .then((res) => { if (alive) setData(res.data); })
            .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Could not load Trust Center"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    if (loading) {
        return (
            <div data-testid="compliance-loading" className="min-h-[60vh] flex items-center justify-center text-slate-400">
                <Loader2 className="animate-spin" size={28} />
            </div>
        );
    }
    if (error || !data) {
        return (
            <div data-testid="compliance-error" className="min-h-[60vh] flex flex-col items-center justify-center text-rose-600 gap-2">
                <AlertTriangle size={28} />
                <div>{error || "Compliance data unavailable"}</div>
            </div>
        );
    }

    const company = data.company || {};
    const frameworks = data.frameworks || [];
    const subprocessors = data.subprocessors || [];

    // ----- schema.org/Organization + hasCredential mapping -----
    // Surfaces SOC 2 / ISO 27001 / PDPPL etc. as machine-readable credentials
    // so security-reviewer Google queries like "Toothmatrix SOC 2" can land on
    // this page directly with a knowledge-panel rich-result. We mark frameworks
    // with status `compliant` / `controls_implemented` as `EducationalOccupationalCredential`
    // (schema.org's most-applicable type for compliance attestations).
    // Spec: https://schema.org/Organization · https://schema.org/hasCredential
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: company.legal_name,
        url: typeof window !== "undefined" ? window.location.origin : undefined,
        sameAs: ["https://toothrocket.com", "https://toothmatrix.com"],
        foundingDate: String(company.founded || ""),
        address: { "@type": "PostalAddress", addressLocality: company.headquarters },
        contactPoint: [
            { "@type": "ContactPoint", contactType: "Data Protection Officer", email: company.data_protection_officer },
            { "@type": "ContactPoint", contactType: "Security", email: company.security_contact },
        ],
        softwareVersion: VERSION.version,
        releaseNotes: `Iteration ${VERSION.iteration} (${VERSION.total_releases} total releases shipped). Released ${VERSION.released_at}.`,
        hasCredential: frameworks.map((f) => ({
            "@type": "EducationalOccupationalCredential",
            name: f.name,
            description: f.full_name,
            credentialCategory: f.status === "compliant" ? "compliance" : "controls_implemented",
            recognizedBy: {
                "@type": "Organization",
                name:
                    f.id === "soc2" ? "AICPA"
                        : f.id === "iso27001" ? "ISO/IEC"
                            : f.id === "gdpr" ? "European Commission"
                                : f.id === "pdppl" ? "Qatar Ministry of Communications"
                                    : f.id === "hipaa" ? "US Department of Health & Human Services"
                                        : "Issuing body",
            },
        })),
    };

    return (
        <div data-testid="compliance-page" className="bg-slate-50 min-h-screen">
            {/* JSON-LD for search-engine rich results (Google "Toothmatrix SOC 2" → knowledge panel) */}
            <script
                type="application/ld+json"
                data-testid="compliance-jsonld"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />
            {/* ----- Hero ----- */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: "radial-gradient(circle at 20% 20%, rgba(34,211,238,0.4) 0, transparent 50%), radial-gradient(circle at 80% 80%, rgba(99,102,241,0.4) 0, transparent 50%)",
                }} />
                <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-[12px] font-mono-tr tracking-[0.18em] uppercase">
                        <Sparkles size={12} className="text-amber-300" />
                        Trust Center
                    </div>
                    <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                        Compliance, controls,<br />and the receipts.
                    </h1>
                    <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
                        {company.product} is built for clinics, labs and regulators who need to verify — not take our word for it. PDPPL · SOC 2 Type II · ISO 27001:2022 · GDPR · HIPAA-aligned. One audit pack. One PDF.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <a
                            href={`${API}/trust/audit-pack.pdf`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="compliance-download-audit-pack"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-[14px] transition shadow-lg shadow-cyan-500/30"
                        >
                            <Download size={16} /> Download Audit Pack (PDF)
                        </a>
                        <a
                            href={`${API}/trust/dpa.pdf`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="compliance-download-dpa"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 font-semibold text-[14px] transition"
                        >
                            <FileText size={16} /> Data Processing Agreement
                        </a>
                    </div>
                    <p className="mt-3 text-[12px] text-slate-400">
                        Tip: the audit pack and DPA require a free Toothmatrix account so we can scope your tenant. <Link to="/login" className="underline hover:text-white">Sign in</Link>.
                    </p>
                </div>
            </section>

            {/* ----- Frameworks ----- */}
            <section className="max-w-6xl mx-auto px-6 py-14 lg:py-20">
                <div className="flex items-center gap-3 mb-6">
                    <ShieldCheck className="text-cyan-600" size={20} />
                    <h2 className="text-2xl font-semibold text-slate-900">Frameworks & certifications</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {frameworks.map((f) => {
                        const badge = STATUS_BADGE[f.status] || { cls: "bg-slate-100 text-slate-700 border-slate-200", label: f.status };
                        return (
                            <article
                                key={f.id}
                                data-testid={`compliance-framework-${f.id}`}
                                className="rounded-xl bg-white border border-slate-200 p-5 hover:border-cyan-300 hover:shadow-sm transition"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="font-semibold text-slate-900 text-[15px]">{f.name}</div>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium border ${badge.cls}`}>
                                        <CheckCircle2 size={10} /> {badge.label}
                                    </span>
                                </div>
                                <div className="mt-2 text-[12.5px] text-slate-500 leading-relaxed">{f.full_name}</div>
                                {f.target_audit && (
                                    <div className="mt-3 text-[11.5px] font-mono-tr tracking-[0.12em] uppercase text-amber-700">
                                        External audit · {f.target_audit}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* ----- Subprocessors ----- */}
            <section className="bg-white border-y border-slate-200">
                <div className="max-w-6xl mx-auto px-6 py-14 lg:py-20">
                    <div className="flex items-center gap-3 mb-6">
                        <Server className="text-indigo-600" size={20} />
                        <h2 className="text-2xl font-semibold text-slate-900">Subprocessors</h2>
                    </div>
                    <p className="text-[14px] text-slate-600 mb-6 max-w-3xl">
                        We use a short, intentional list of vendors. Each is bound by a written Data Processing Agreement. Customers receive 30 days' notice before any new subprocessor is added to this list.
                    </p>
                    <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="min-w-full text-[13px]">
                            <thead className="bg-slate-50 text-left text-[11.5px] font-mono-tr tracking-[0.12em] uppercase text-slate-500">
                                <tr>
                                    <th className="px-4 py-2.5">Vendor</th>
                                    <th className="px-4 py-2.5">Purpose</th>
                                    <th className="px-4 py-2.5">Data classes</th>
                                    <th className="px-4 py-2.5">Location</th>
                                    <th className="px-4 py-2.5" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {subprocessors.map((s, i) => (
                                    <tr key={i} data-testid={`compliance-subprocessor-${i}`} className="hover:bg-slate-50">
                                        <td className="px-4 py-3 font-semibold text-slate-800">{s.name}</td>
                                        <td className="px-4 py-3 text-slate-600">{s.purpose}</td>
                                        <td className="px-4 py-3 text-slate-500">{(s.data_classes || []).join(", ")}</td>
                                        <td className="px-4 py-3 text-slate-500">{s.location}</td>
                                        <td className="px-4 py-3 text-right">
                                            {s.url && (
                                                <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 text-[12px]">
                                                    Privacy <ExternalLink size={11} />
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* ----- Company / contacts ----- */}
            <section className="max-w-6xl mx-auto px-6 py-14 lg:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div data-testid="compliance-company" className="rounded-xl bg-white border border-slate-200 p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Globe className="text-cyan-600" size={16} />
                            <div className="font-semibold text-slate-900">Company</div>
                        </div>
                        <dl className="text-[13px] space-y-1.5">
                            <div><dt className="text-slate-500">Legal entity</dt><dd className="text-slate-800">{company.legal_name}</dd></div>
                            <div className="flex items-center gap-1.5 text-slate-500"><MapPin size={12} /> {company.headquarters}</div>
                            <div className="text-slate-500">Founded · {company.founded}</div>
                        </dl>
                    </div>
                    <div data-testid="compliance-dpo" className="rounded-xl bg-white border border-slate-200 p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Lock className="text-emerald-600" size={16} />
                            <div className="font-semibold text-slate-900">Data Protection Officer</div>
                        </div>
                        <div className="text-[13px] text-slate-600 mb-2">For data-subject access requests, deletion, portability, or any PDPPL/GDPR right.</div>
                        <a href={`mailto:${company.data_protection_officer}`} className="inline-flex items-center gap-1.5 text-cyan-700 hover:text-cyan-900 text-[13.5px] font-medium">
                            <Mail size={13} /> {company.data_protection_officer}
                        </a>
                    </div>
                    <div data-testid="compliance-security" className="rounded-xl bg-white border border-slate-200 p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <ShieldCheck className="text-rose-600" size={16} />
                            <div className="font-semibold text-slate-900">Security disclosure</div>
                        </div>
                        <div className="text-[13px] text-slate-600 mb-2">{company.bug_bounty}</div>
                        <a href={`mailto:${company.security_contact}`} className="inline-flex items-center gap-1.5 text-rose-700 hover:text-rose-900 text-[13.5px] font-medium">
                            <Mail size={13} /> {company.security_contact}
                        </a>
                    </div>
                </div>
                {data.uptime_target && (
                    <div className="mt-6 text-center text-[12.5px] text-slate-500">
                        Uptime target · <span className="font-mono-tr text-slate-800">{data.uptime_target}</span>
                        {data.last_updated && <> · Last updated <span className="font-mono-tr text-slate-700">{String(data.last_updated).slice(0, 10)}</span></>}
                    </div>
                )}
                {/* Build provenance — proof that this is a tested, evolving codebase, not a launch-week MVP. */}
                <div
                    data-testid="compliance-build-line"
                    className="mt-3 flex flex-wrap items-center justify-center gap-2 text-[11.5px] font-mono-tr tracking-[0.12em] uppercase text-slate-400"
                >
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                        <Sparkles size={10} className="text-cyan-500" />
                        Build <span className="text-slate-800">v{VERSION.version}</span>
                    </span>
                    <span>·</span>
                    <span>
                        <span className="text-slate-700">{VERSION.total_releases}</span> production releases
                    </span>
                    <span>·</span>
                    <span>shipped <span className="text-slate-700">{VERSION.released_at}</span></span>
                </div>
            </section>
        </div>
    );
}
